import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Copy, ChevronRight, Loader2 } from "lucide-react";
import { StepIndicator } from "./StepIndicator";

export function SaleSmartlyGuidedSetup({ integration, formData, setFormData, webhookInfo, handleUpdate, handleTest, testing, copyToClipboard }) {
  const [currentStep, setCurrentStep] = useState(1);
  const creds = formData.credentials || {};
  const hasWebhookKey = !!creds.webhook_key;

  const steps = [
    { step: 1, label: "Get Your Webhook Key from SaleSmartly" },
    { step: 2, label: "Configure Callback URL in SaleSmartly" },
    { step: 3, label: "Select Events & Test" },
  ];

  const effectiveStep = !hasWebhookKey ? 1 : currentStep < 2 ? 2 : currentStep;

  return (
    <div className="space-y-4" data-testid="sm-guided-setup">
      <div className="space-y-2">
        {steps.map(s => (
          <button key={s.step} onClick={() => setCurrentStep(s.step)} className="w-full text-left">
            <StepIndicator step={s.step} currentStep={effectiveStep < s.step ? effectiveStep : currentStep >= s.step ? currentStep : effectiveStep} label={s.label} />
          </button>
        ))}
      </div>

      {currentStep === 1 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 bg-[#0868F7]/5 border border-[#0868F7]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 1: Get Your Webhook Key</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">1.</span>
                <span>Log in to <a href="https://www.salesmartly.com" target="_blank" rel="noopener noreferrer" className="text-[#0868F7] underline font-medium">SaleSmartly</a> with your Enterprise/Max account</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">2.</span>
                <span>Go to <strong>Settings</strong> → <strong>Project Management</strong> → <strong>Enterprise Development Configuration</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">3.</span>
                <span>Find the <strong>Webhook Key</strong> and copy it</span>
              </li>
            </ol>
          </div>
          <div className="space-y-2">
            <Label className="font-medium">Webhook Key</Label>
            <Input
              type="password"
              value={creds.webhook_key || ""}
              onChange={(e) => setFormData({ ...formData, credentials: { ...creds, webhook_key: e.target.value } })}
              placeholder="Your SaleSmartly Webhook Key"
              className="border-gray-300 rounded-lg font-mono text-sm"
              data-testid="sm-webhook-key"
            />
          </div>
          <Button onClick={() => { handleUpdate(); setCurrentStep(2); }} disabled={!creds.webhook_key} className="w-full bg-[#0868F7] hover:bg-[#0656D0] text-white rounded-lg" data-testid="sm-step1-next">
            Save & Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 bg-[#0868F7]/5 border border-[#0868F7]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 2: Set Callback URL in SaleSmartly</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">1.</span>
                <span>In SaleSmartly, go to <strong>Settings</strong> → <strong>Project Management</strong> → <strong>Enterprise Development Configuration</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">2.</span>
                <span>Paste the <strong>Callback URL</strong> below into the webhook URL field</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">3.</span>
                <span>Enable the webhook and save</span>
              </li>
            </ol>
          </div>

          {webhookInfo && (
            <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div>
                <Label className="text-xs font-medium text-gray-500 mb-1 block">Callback URL</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-white px-3 py-2.5 rounded-lg border border-gray-200 font-mono break-all select-all">{webhookInfo.webhook_url}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.webhook_url)} className="shrink-0 h-9"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-gray-500 mb-1 block">Verify Token</Label>
                <div className="flex gap-2">
                  <code className="flex-1 text-xs bg-white px-3 py-2.5 rounded-lg border border-gray-200 font-mono select-all">{webhookInfo.verify_token}</code>
                  <Button variant="outline" size="sm" onClick={() => copyToClipboard(webhookInfo.verify_token)} className="shrink-0 h-9"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentStep(1)} className="border-gray-300 rounded-lg">Back</Button>
            <Button onClick={() => setCurrentStep(3)} className="flex-1 bg-[#0868F7] hover:bg-[#0656D0] text-white rounded-lg" data-testid="sm-step2-next">
              I've Done This <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="space-y-4 pt-2">
          <div className="p-4 bg-[#0868F7]/5 border border-[#0868F7]/15 rounded-xl">
            <h4 className="font-semibold text-sm text-gray-900 mb-3">Step 3: Select Events & Test</h4>
            <ol className="space-y-3 text-sm text-gray-700">
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">1.</span>
                <span>In SaleSmartly webhook settings, subscribe to: <strong>New message notifications</strong> and <strong>Customer information sync</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">2.</span>
                <span>For message notifications, select <strong>both customer and member info</strong> to capture maximum data</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-[#0868F7] shrink-0">3.</span>
                <span>Click <strong>Test Connection</strong> below to verify</span>
              </li>
            </ol>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
            <h5 className="text-sm font-semibold text-gray-900">Supported Channels</h5>
            <div className="flex flex-wrap gap-1.5">
              {["WhatsApp API", "Messenger", "LINE", "Instagram", "Telegram", "Email", "TikTok", "Chat Plugin"].map(ch => (
                <span key={ch} className="px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600">{ch}</span>
              ))}
            </div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
            <h5 className="text-sm font-semibold text-gray-900">Configuration Summary</h5>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-gray-500">Webhook Key:</span> <span className={creds.webhook_key ? "text-emerald-600 font-medium" : "text-red-500"}>{creds.webhook_key ? "Configured" : "Missing"}</span></div>
              <div><span className="text-gray-500">Callback URL:</span> <span className={webhookInfo ? "text-emerald-600 font-medium" : "text-red-500"}>{webhookInfo ? "Ready" : "Not generated"}</span></div>
              <div><span className="text-gray-500">Status:</span> {integration.status === "connected" ? <span className="text-emerald-600 font-medium">Connected</span> : <span className="text-amber-600 font-medium">Pending test</span>}</div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentStep(2)} className="border-gray-300 rounded-lg">Back</Button>
            <Button variant="outline" onClick={handleTest} disabled={testing} className="flex-1 border-[#0868F7] text-[#0868F7] hover:bg-[#0868F7]/5" data-testid="sm-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Test Connection
            </Button>
          </div>

          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-xs text-emerald-800 flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span><strong>How it works:</strong> When a customer sends a message through any channel connected to SaleSmartly (WhatsApp, Messenger, Instagram, etc.), or when a new customer is created/updated, SaleSmartly pushes the data to POMEN via webhook. A lead is automatically created with all available info (name, email, phone, channel, country, tags, etc.).</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
